import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { readOpenConversationId, writeOpenConversationId } from './helpers/openConversationStorage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isGoneError, useFetchConversation } from 'dataQueries/conversations.query'

import { ConversationStatus, ConversationType } from 'models/conversations'
import constate from 'constate'
import { logger } from 'utils/logging'
import { useConversationSocket } from 'utils/conversationSocket'
import { useQueryClient } from '@tanstack/react-query'
import { useIsDocumentVisible } from './helpers/useIsDocumentVisible'
import { useResolvedCurrentUserId } from './helpers/useResolvedCurrentUserId'
import { useUserData } from 'components/contexts/UserDataContext'

/** Visual state of the chat widget. */
export type ChatWidgetMode = 'closed' | 'reduced' | 'expanded'

/** Safety window after which a "typing" indicator clears if no stop/refresh event arrives. */
const TYPING_CLEAR_MS = 5_000

type RestoreOutcome = 'restored' | 'dropped_closed' | 'dropped_not_found' | 'unverified'

/**
 * Holds the chat widget's open/closed state and the currently selected conversation.
 * Kept separate from the socket provider so UI state and the connection lifecycle evolve independently.
 */
const useChatWidgetState = () => {
  const [mode, setMode] = useState<ChatWidgetMode>('closed')
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)

  // Shared "someone else is typing" set, keyed by conversationId — consumed by both the thread view
  // and the conversation list. Tracked here (inside the socket + widget providers) so a single typing
  // subscription feeds every consumer. The backend broadcasts typing to all subscribers including the
  // sender, so we filter out our own id.
  const { registerTypingHandler, registerMessageHandler } = useConversationSocket()
  const currentUserId = useResolvedCurrentUserId()
  const [typingUserIds, setTypingUserIds] = useState<ReadonlyMap<string, string>>(new Map())
  const typingConversationIds = useMemo<ReadonlySet<string>>(() => new Set(typingUserIds.keys()), [typingUserIds])
  const typingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const timers = typingTimersRef.current

    const stopTyping = (conversationId: string) => {
      const timer = timers.get(conversationId)
      if (timer) clearTimeout(timer)
      timers.delete(conversationId)
      setTypingUserIds((prev) => {
        if (!prev.has(conversationId)) return prev
        const next = new Map(prev)
        next.delete(conversationId)
        return next
      })
    }

    const unregisterTyping = registerTypingHandler((typing) => {
      if (typing.userId === currentUserId) return
      if (!typing.isTyping) {
        stopTyping(typing.conversationId)
        return
      }
      setTypingUserIds((prev) => (prev.get(typing.conversationId) === typing.userId ? prev : new Map(prev).set(typing.conversationId, typing.userId)))
      const existing = timers.get(typing.conversationId)
      if (existing) clearTimeout(existing)
      timers.set(typing.conversationId, setTimeout(() => stopTyping(typing.conversationId), TYPING_CLEAR_MS))
    })
    const unregisterMessages = registerMessageHandler((message) => {
      if (message.senderId === currentUserId) return
      stopTyping(message.conversationId)
    })
    return () => {
      unregisterTyping()
      unregisterMessages()
    }
  }, [registerTypingHandler, registerMessageHandler, currentUserId])

  // The conversation whose thread is actually rendered right now. Registered by the mounted
  // `ConversationThread` itself, because `selectedConversationId` lingers after the widget closes or the
  // popover goes back to its list, and so says nothing about what is on screen.
  const [renderedThreadIds, setRenderedThreadIds] = useState<string[]>([])
  const renderedThreadId = renderedThreadIds[renderedThreadIds.length - 1] ?? null
  const registerRenderedThread = useCallback((conversationId: string) => {
    setRenderedThreadIds((current) => [...current, conversationId])
    return () => setRenderedThreadIds((current) => {
      const index = current.lastIndexOf(conversationId)
      return index === -1 ? current : [...current.slice(0, index), ...current.slice(index + 1)]
    })
  }, [])

  // A thread is "on screen" only while the widget is open on it AND the tab is visible. Only that thread
  // skips unread bumps and desktop notifications; in every other case (tab hidden, widget closed, another
  // thread open) its new messages count as unread and notify (QA-398).
  const isDocumentVisible = useIsDocumentVisible()
  const onScreenConversationId = mode !== 'closed' && isDocumentVisible ? renderedThreadId : null


  // Stable /user/me id rather than the participant id above, which can change while the list loads and would
  // move the storage key under our feet.
  const { baseUserData } = useUserData()
  const storageUserId = baseUserData?.internalId

  // The open conversation is mirrored to storage so opening the widget in another tab lands in the same
  // thread (QA-377). It is only adopted on opening: a tab never pops the widget open by itself, and a
  // thread on screen is never swapped out under the user by another tab.
  const fetchConversation = useFetchConversation()
  const queryClient = useQueryClient()
  const restoringIdRef = useRef<string | null>(null)
  const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(null)

  const modeRef = useRef(mode)
  modeRef.current = mode

  // Checked against the server first: the stored thread may have been closed or be gone since another tab
  // stored it, and opening it would show an empty or read-only thread instead of the conversation list.
  const restoreConversation = useCallback(async (conversationId: string) => {
    restoringIdRef.current = conversationId
    const outcome = await fetchConversation(conversationId).then(
      (conversation): RestoreOutcome => (conversation.status === ConversationStatus.CLOSED ? 'dropped_closed' : 'restored'),
      (error): RestoreOutcome => (isGoneError(error) ? 'dropped_not_found' : 'unverified')
    )
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_OPEN_CONVERSATION_RESTORE, { outcome })

    const isSuperseded = restoringIdRef.current !== conversationId
    restoringIdRef.current = null

    if (outcome === 'restored') {
      if (isSuperseded) return
      setSelectedConversationId(conversationId)
      // The popover only opens a thread its ACTIVE list holds, and this tab's list may predate it.
      await queryClient.invalidateQueries({ queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] })
      return
    }
    if (outcome === 'unverified') return

    // Another tab (or this one, meanwhile) may have stored a different conversation since.
    if (readOpenConversationId(storageUserId) === conversationId) writeOpenConversationId(storageUserId, null)
    logger.info('ChatWidget: dropped the stored open conversation', undefined, {
      context: { component: 'ChatWidget', action: 'restoreOpenConversation', metadata: { outcome } },
    })
  }, [fetchConversation, queryClient, storageUserId])

  const adoptStoredConversation = useCallback(() => {
    if (modeRef.current !== 'closed') return
    const storedId = readOpenConversationId(storageUserId)
    if (!storedId) return
    setPendingRestoreId(storedId)
    void restoreConversation(storedId).finally(() =>
      setPendingRestoreId((current) => (current === storedId ? null : current)))
  }, [storageUserId, restoreConversation])

  const openReduced = useCallback(() => {
    adoptStoredConversation()
    setMode('reduced')
  }, [adoptStoredConversation])
  const expand = useCallback(() => {
    adoptStoredConversation()
    setMode('expanded')
  }, [adoptStoredConversation])
  const close = useCallback(() => setMode('closed'), [])

  const applySelection = useCallback((conversationId: string | null) => {
    restoringIdRef.current = null
    setPendingRestoreId(null)
    setSelectedConversationId(conversationId)
    writeOpenConversationId(storageUserId, conversationId)
  }, [storageUserId])

  /** Set the active conversation without changing the widget mode (use after sending a message
   * to keep the popover open instead of forcing it into expanded mode). */
  const setActiveConversation = useCallback((conversationId: string | null) => {
    applySelection(conversationId)
  }, [applySelection])

  /** Select a conversation AND expand to drawer view — used when the user explicitly opens a
   * conversation from the list. */
  const selectConversation = useCallback((conversationId: string) => {
    applySelection(conversationId)
    setMode('expanded')
  }, [applySelection])

  const [isNewConversationRequested, setIsNewConversationRequested] = useState(false)

  const startNewConversation = useCallback((closedConversationType?: ConversationType) => {
    logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_NEW_CONVERSATION_FROM_CLOSED_THREAD, { conversationType: closedConversationType ?? null })
    applySelection(null)
    setIsNewConversationRequested(true)
    setMode('reduced')
  }, [applySelection])

  const consumeNewConversationRequest = useCallback(() => setIsNewConversationRequested(false), [])

  return {
    mode,
    selectedConversationId,
    currentUserId,
    isRestoring: pendingRestoreId !== null,
    onScreenConversationId,
    isDocumentVisible,
    registerRenderedThread,
    typingConversationIds,
    typingUserIds,
    openReduced,
    expand,
    close,
    selectConversation,
    setActiveConversation,
    isNewConversationRequested,
    startNewConversation,
    consumeNewConversationRequest,
  }
}

export const [ChatWidgetProvider, useChatWidget] = constate(useChatWidgetState)
